import { type CONVEYANCE_TYPES, type Location } from '@koala/sdk';
import { CONVEYANCE_TYPES as CONVEYANCE_TYPES_ENUM } from '@koala/sdk/v4';
import { useEffect, useState, useMemo, useRef } from 'react';
import { addMinutes, isSameDay, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import {
  StyledLoader,
  StyledTimeSelect,
  StyledItalicSelectContainer,
  StyledTimePickerContainer,
  StyledTimePickerLabel,
  StyledChangedMarker,
  StyledPendingChangeWarning,
  StyledPendingChangeWarningIcon,
  StyledPendingChangeWarningText,
  StyledTimePicker,
  StyledRadioContainer,
  StyledRadioOption,
  StyledRadioInput,
  StyledRadioContent,
  StyledRadioLabel,
  StyledRadioSubtitle,
  StyledUnavailableBadge,
  StyledFieldset,
  StyledLegend,
  StyledScheduledUnavailableMessage,
  StyledNoTimesMessage,
  StyledOrderingUnavailable,
  StyledOrderingUnavailableIcon,
  StyledOrderingUnavailableTitle,
  StyledOrderingUnavailableSubtitle,
  StyledOrderingUnavailablePrimaryText,
} from '@/features/handoff/time-picker/styles';
import {
  useHandoffTimes,
  useNextAvailableDayWithSlots,
} from '@/features/handoff/time-picker/use-handoff-times';
import { getAvailableDays } from '@/features/handoff/time-picker/utils';
import GenericErrorBoundary from '@/components/genericErrorBoundary';
import { Select } from '@/components/uielements/forms/select';
import { useSelector } from '@/redux';
import { getFormattedDateInTimezone } from '@/utils/dates';
import { getLeadTimes, getStoreTimeInfo, supportsAsapOrders } from '@/utils/locations';
import {
  getFulfillmentTime,
  getReadableRange,
  isAsapAvailable,
  getEarliestPrepTime,
  getPrepMinutes,
  isAsapValue,
  getFirstAvailableTimeMs,
} from '@/utils/conveyance';
import Label from '@/components/uielements/label';
import { TIME_MODES } from '@/constants/global';
import { Render } from '@/components/uielements/render';
import {
  StyledCancelButton,
  StyledConfirmButton,
  StyledModalFooter,
} from '@/components/checkout/orderTime/styles';
import { DATE_FORMAT } from '@/constants/dates';
import { ROUTES } from '@/constants/routes';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';

interface Props {
  isSubmitting?: boolean;
  value?: string;
  location: Location;
  handoffType: CONVEYANCE_TYPES;
  stack?: boolean;
  onCancel?: () => void;
  /**
   * Applies the selection. Return (or resolve) `true` to say the commit was
   * deferred to a confirmation of the surface's own, so the pending treatment is
   * not cleared — the diner has not committed anything yet.
   *
   * Note what this does and does not guarantee. It keeps the per-field touched
   * flags, which is what makes the treatment reappear if the diner declines the
   * confirmation. It does NOT keep the treatment visible *while* the confirmation
   * is open: the menu page passes `resetOrderTime ?? time_wanted` as `value`, and
   * the deferral sets `resetOrderTime` to the candidate time, so `value` briefly
   * equals the selection and both comparisons go false. That window is covered by
   * the confirmation itself, which overlays the picker.
   */
  onSubmit?: (selectedWantedAt: string) => Promise<boolean | void> | boolean | void;
  /**
   * Opt in to the pending (uncommitted) selection treatment: the CHANGED field
   * markers and the inline warning above the button row. Only the two surfaces
   * covered by the designs pass this (the storefront menu page and checkout) — the
   * remaining call sites are deliberately left untouched (TEAM-14386, TEAM-14218).
   */
  showPendingChange?: boolean;
  /**
   * Allow the commit ("Update Time") button even when a delivery order has no
   * saved delivery address yet. The delivery details form embeds this picker
   * ABOVE its own address fields and collects the address in the same form, so
   * gating the commit on a redux delivery address (which only lands on Continue)
   * would leave "Update Time" permanently disabled there and drop the diner's
   * time (TEAM-14388). Only the delivery form opts in; everywhere else the
   * address must exist first.
   */
  allowCommitWithoutDeliveryAddress?: boolean;
}

/** Flags a field whose selection is changed but not yet committed (TEAM-14386). */
function ChangedMarker({ show }: { show: boolean }) {
  return (
    <Render condition={show}>
      <StyledChangedMarker>CHANGED</StyledChangedMarker>
    </Render>
  );
}

export function HandoffTimePicker({
  isSubmitting,
  value,
  location,
  handoffType,
  stack = true,
  onCancel,
  onSubmit,
  showPendingChange = false,
  allowCommitWithoutDeliveryAddress = false,
}: Props) {
  const { storeTimezone, hasRemainingHoursToday, storeDate, nextAvailableDateInStore, isOpen } =
    getStoreTimeInfo(location, handoffType as string);
  const router = useRouter();
  const supportsScheduledOrders = Boolean(location.order_ahead);
  const supportsAsap = supportsAsapOrders(location);
  const isAsapAvailableFlag = isAsapAvailable(location, handoffType);
  const isOrderingUnavailable = !supportsAsap && !supportsScheduledOrders;
  const [timeMode, setTimeMode] = useState(
    !supportsScheduledOrders || (isAsapAvailableFlag && !value)
      ? TIME_MODES.ASAP
      : TIME_MODES.SCHEDULED,
  );
  const isScheduling = supportsScheduledOrders && timeMode === TIME_MODES.SCHEDULED;
  // The committed day behind the `value` prop. Also the picker's initial selection,
  // so it is derived once and reused rather than formatted twice.
  const committedDay = getFormattedDateInTimezone(
    value ?? '',
    DATE_FORMAT.YEAR_MONTH_DAY_DASHED,
    storeTimezone,
  );
  const [selectedWantedAt, setSelectedWantedAt] = useState<string>(value ?? '');
  const [selectedDay, setSelectedDay] = useState(committedDay);
  // Diner intent, tracked separately from the values themselves. Only the two
  // Select onChange handlers below set these flags; every other write to
  // selectedDay / selectedWantedAt in this file is programmatic (initial seeding,
  // the one-shot auto-advance, the ASAP force, the first-slot reset when the day
  // changes, and the menu-page conveyance re-select). AC6 requires those to leave
  // the picker looking clean, and a value diff alone cannot tell them apart: the
  // first-slot reset rewrites the time whenever the date changes, so comparing
  // values only would mark BOTH fields on a date-only change and fail AC1.
  const [dateTouched, setDateTouched] = useState(false);
  const [timeTouched, setTimeTouched] = useState(false);
  const datePending = showPendingChange && dateTouched && selectedDay !== committedDay;
  const timePending = showPendingChange && timeTouched && selectedWantedAt !== (value ?? '');
  const hasPendingChange = isScheduling && (datePending || timePending);
  const { type: conveyanceType, address: deliveryAddress } = useSelector(selectConveyance);
  const isDelivery = Boolean(conveyanceType === CONVEYANCE_TYPES_ENUM.DELIVERY);
  const requiresDelivery = isDelivery && !deliveryAddress?.street_address;

  const { checkoutBasket } = useSelector((state) => state.app.basket);
  const { earliest_ready_time_at_location } = checkoutBasket || {};
  const leadTime = getLeadTimes(location, handoffType);
  const prepMinutes = getPrepMinutes(checkoutBasket, router, location, handoffType);
  const asapMinuteRangeTitle =
    timeMode === TIME_MODES.ASAP && isAsapValue(value)
      ? ` (${getReadableRange(prepMinutes)})`
      : ` (${getReadableRange(leadTime)})`;

  const asapMinuteRangeSubTitle =
    timeMode === TIME_MODES.ASAP && isAsapValue(value)
      ? ` ${getReadableRange(prepMinutes)}`
      : ` ${getReadableRange(leadTime)}`;

  // Get a list of days that the order can be scheduled for. The full window
  // (today + advance_notice_days .. order_ahead_days) is always selectable —
  // days are never removed based on slot availability, so the dropdown can't
  // drain to empty and the diner can always pick any valid day (TEAM-14095).
  const availableDaysOptions = useMemo(
    () => getAvailableDays(location, handoffType),
    [location, handoffType],
  );

  // Initial fetch for available handoff times for the selected day.
  // This is using react query cache
  const { isLoading, isError, handoffTimeSelectOptions } = useHandoffTimes({
    basketId: checkoutBasket?.id,
    dayWanted: selectedDay,
    handoffType,
    location,
  });

  // Cached lookup of the first day in the window that actually has slots. This
  // is a single, stable-keyed react-query entry (it does not depend on
  // selectedDay), so it can't trigger the per-day refetch storm the old
  // slot-driven effect caused. It only reads availability — it never mutates
  // availableDaysOptions. Drives the one-shot auto-advance below.
  const { data: nextAvailableDayWithSlots } = useNextAvailableDayWithSlots({
    availableDays: availableDaysOptions,
    handoffType,
    location,
  });
  const earliestReadyTime = getEarliestPrepTime(
    earliest_ready_time_at_location || '',
    handoffTimeSelectOptions,
  );

  // Filter handoffTimeSelectOptions to only include times that are greater than earliestReadyTime
  // If NOT asap adjusted we want to include the current earliestReadyTime, since it will be later than normal
  let handoffTimeOptions =
    earliestReadyTime &&
    isAsapValue(selectedWantedAt) &&
    new Date(handoffTimeSelectOptions[0]?.value).getTime() < new Date(earliestReadyTime).getTime()
      ? handoffTimeSelectOptions.filter((option) => {
          return checkoutBasket?.is_asap_adjusted
            ? new Date(option.value).getTime() > new Date(earliestReadyTime).getTime()
            : new Date(option.value).getTime() >= new Date(earliestReadyTime).getTime();
        })
      : handoffTimeSelectOptions;

  // When the store's ready buffer is meaningful and the selected day is either
  // in the future or the store isn't currently open, remove any option earlier
  // than the store's first-open time on that day plus prep_minutes — those
  // slots would be rejected downstream because the store hasn't opened and
  // reached its ready buffer yet.
  if (
    router.route === ROUTES.CHECKOUT &&
    checkoutBasket &&
    checkoutBasket.extended_prep_time &&
    checkoutBasket.prep_minutes >= leadTime &&
    (selectedDay > storeDate || !isOpen)
  ) {
    const earliestHandoffTime = getFirstAvailableTimeMs(
      location,
      handoffType,
      selectedDay,
      storeTimezone,
      checkoutBasket.prep_minutes,
    );

    if (earliestHandoffTime !== null) {
      handoffTimeOptions = handoffTimeOptions.filter(
        (option) => new Date(option.value).getTime() >= earliestHandoffTime,
      );
    }
  }

  // Pick an initial day only when none is selected yet, or when today is no
  // longer orderable. This is keyed off the (stable) day list — never off slot
  // availability — and `selectedDay` is intentionally not a dependency, so it
  // never overrides a manual selection. Removing the old slot-driven
  // auto-advance effect is what stops the picker from cycling through dates and
  // racing itself into an unresponsive state (TEAM-14095). A selected day with
  // no slots simply shows no times, and the diner can pick another day.
  useEffect(() => {
    if (
      availableDaysOptions.length > 0 &&
      (!selectedDay || (!hasRemainingHoursToday && selectedDay === storeDate))
    ) {
      setSelectedDay(availableDaysOptions.find((day) => !day.disabled)?.value ?? '');
    }
  }, [hasRemainingHoursToday, availableDaysOptions, storeDate]);

  // If the auto-selected initial day has no generated slots, advance ONCE to the
  // first day that actually has availability. This is the safe replacement for
  // the slot-driven auto-advance effect removed in TEAM-14095: it fires at most
  // once (ref-latched), reads the cached useNextAvailableDayWithSlots result,
  // and never removes days from availableDaysOptions — so it can neither cycle
  // through dates nor drain the dropdown to empty. It only seeds the initial
  // landing; a later manual pick of an empty day is respected (shows no times).
  const didAutoAdvanceToSlotsRef = useRef(false);
  useEffect(() => {
    if (didAutoAdvanceToSlotsRef.current) return;
    if (timeMode !== TIME_MODES.SCHEDULED) return;
    if (isLoading || !selectedDay || selectedWantedAt) return;
    if (handoffTimeSelectOptions.length > 0) return;

    const nextDayWithSlots = nextAvailableDayWithSlots?.day?.value;
    if (!nextDayWithSlots || nextDayWithSlots === selectedDay) return;

    didAutoAdvanceToSlotsRef.current = true;
    setSelectedDay(nextDayWithSlots);
  }, [
    isLoading,
    selectedDay,
    selectedWantedAt,
    timeMode,
    handoffTimeSelectOptions,
    nextAvailableDayWithSlots,
  ]);

  useEffect(() => {
    if (!supportsScheduledOrders && timeMode === TIME_MODES.SCHEDULED) {
      setTimeMode(TIME_MODES.ASAP);
    }
  }, [supportsScheduledOrders, timeMode]);

  // Set the initial scheduled order selected time option, if we already have a value skip
  // And set selected time if the selected day gets updated
  useEffect(() => {
    if (
      handoffTimeOptions.length === 0 ||
      timeMode === TIME_MODES.ASAP ||
      (timeMode === TIME_MODES.SCHEDULED &&
        selectedWantedAt &&
        isSameDay(parseISO(selectedWantedAt), parseISO(selectedDay)))
    ) {
      return;
    }
    setSelectedWantedAt(handoffTimeOptions[0].value);
  }, [handoffTimeOptions, timeMode, selectedWantedAt, selectedDay]);

  // Select first time when switching conveyance tabs for scheduled orders, only needed for the menu page
  useEffect(() => {
    if (
      router.route !== ROUTES.CHECKOUT &&
      timeMode === TIME_MODES.SCHEDULED &&
      handoffTimeOptions.length > 0
    ) {
      setSelectedWantedAt(handoffTimeOptions[0].value);
    }
  }, [handoffType, timeMode, handoffTimeOptions, router.route]);

  // Nothing on this day can be picked. Drives both the commit gate and, once the
  // busy and failed states are excluded, the no-times message (TEAM-14275).
  const noSelectableTimes = isScheduling && handoffTimeOptions.length === 0;
  // The settled states of the time field have to stay distinguishable. Only a
  // request that came back, succeeded, and carried nothing is "no times" — a
  // request in flight, a submit in progress and a failed request are all
  // zero-length too, and labelling any of them as no availability is what this
  // guards against.
  const hasNoTimes = noSelectableTimes && !isLoading && !isSubmitting && !isError;

  const clearPendingChange = () => {
    setDateTouched(false);
    setTimeTouched(false);
  };

  const commitSelection = async () => {
    const deferred = await onSubmit?.(timeMode === TIME_MODES.ASAP ? '' : selectedWantedAt);
    // A surface may hand the commit to a confirmation of its own — the menu page
    // raises "Restart order" when items are unavailable at the new time. Nothing is
    // committed until the diner answers, so the touched flags must survive. Clearing
    // them here would leave a declined selection sitting in the picker with nothing
    // flagging it as unapplied (TEAM-14386 AC3). See the `onSubmit` docblock for why
    // this keeps the flags but not the visible treatment during the confirmation.
    if (deferred !== true) {
      clearPendingChange();
    }
  };

  // Discard an uncommitted selection.
  //
  // The day is only restored when there is a committed day to restore. An ASAP
  // commit carries no time at all (ASAP_VALUE is ''), which makes `committedDay`
  // an empty string — writing that back blanks the date select AND unmounts the
  // whole time field, and the seeding effect above cannot recover it because
  // `selectedDay` is deliberately not in its dependency array (TEAM-14095). The
  // diner's own day is a better resting place than an empty one; nothing is marked
  // either way, because the touched flags are cleared here.
  const discardPendingChange = () => {
    clearPendingChange();
    setSelectedWantedAt(value ?? '');
    if (committedDay) {
      setSelectedDay(committedDay);
    }
  };

  return (
    <GenericErrorBoundary>
      <StyledTimePicker>
        <Render condition={supportsAsap}>
          <StyledFieldset role="radiogroup" aria-labelledby="time-mode-label">
            <StyledLegend id="time-mode-label">Choose order wanted at time</StyledLegend>
            <StyledRadioContainer>
              <Render condition={supportsAsap}>
                <>
                  <StyledRadioOption
                    disabled={!isAsapAvailableFlag}
                    checked={timeMode === TIME_MODES.ASAP}
                    aria-describedby="asap-description"
                  >
                    <StyledRadioContent>
                      <StyledRadioLabel id="asap-label">
                        ASAP
                        <Render
                          condition={isAsapAvailableFlag}
                          whenTrue={<>{asapMinuteRangeTitle}</>}
                          whenFalse={
                            <StyledUnavailableBadge aria-label="ASAP is currently unavailable">
                              Unavailable
                            </StyledUnavailableBadge>
                          }
                        />
                      </StyledRadioLabel>
                      <StyledRadioSubtitle id="asap-description">
                        <Render
                          condition={isAsapAvailableFlag}
                          whenTrue={<>Ready in {asapMinuteRangeSubTitle}</>}
                          whenFalse={
                            <span>
                              Next available:{' '}
                              {getFulfillmentTime(
                                new Date(
                                  addMinutes(new Date(nextAvailableDateInStore), leadTime),
                                ).toISOString(),
                                location,
                              )}
                            </span>
                          }
                        />
                      </StyledRadioSubtitle>
                    </StyledRadioContent>
                    <StyledRadioInput
                      type="radio"
                      name="handoff-mode"
                      value="asap"
                      onChange={() => isAsapAvailableFlag && setTimeMode(TIME_MODES.ASAP)}
                      checked={timeMode === TIME_MODES.ASAP}
                      disabled={!isAsapAvailableFlag}
                      aria-disabled={!isAsapAvailableFlag}
                      aria-labelledby="asap-label"
                      aria-describedby="asap-description"
                    />
                  </StyledRadioOption>

                  <Render condition={supportsScheduledOrders}>
                    <StyledRadioOption
                      checked={timeMode === TIME_MODES.SCHEDULED}
                      aria-describedby="scheduled-description"
                    >
                      <StyledRadioContent>
                        <StyledRadioLabel id="scheduled-label">Schedule for later</StyledRadioLabel>
                        <StyledRadioSubtitle id="scheduled-description">
                          Choose a specific time
                        </StyledRadioSubtitle>
                      </StyledRadioContent>
                      <StyledRadioInput
                        type="radio"
                        name="handoff-mode"
                        value="scheduled"
                        onChange={() => setTimeMode(TIME_MODES.SCHEDULED)}
                        checked={timeMode === TIME_MODES.SCHEDULED}
                        aria-labelledby="scheduled-label"
                        aria-describedby="scheduled-description"
                      />
                    </StyledRadioOption>
                  </Render>

                  <Render condition={!supportsScheduledOrders}>
                    <StyledScheduledUnavailableMessage>
                      Scheduled ordering is not available at this location.
                    </StyledScheduledUnavailableMessage>
                  </Render>
                </>
              </Render>
            </StyledRadioContainer>
          </StyledFieldset>
        </Render>

        <Render condition={isOrderingUnavailable}>
          <StyledOrderingUnavailable data-testid="ordering-unavailable-message">
            <StyledOrderingUnavailableIcon title="Ordering unavailable" aria-hidden />
            <StyledOrderingUnavailableTitle>
              Ordering is currently unavailable
            </StyledOrderingUnavailableTitle>
            <StyledOrderingUnavailableSubtitle>
              Check back later or{' '}
              <StyledOrderingUnavailablePrimaryText href={`tel:${location.phone_number}`}>
                contact the restaurant directly
              </StyledOrderingUnavailablePrimaryText>
            </StyledOrderingUnavailableSubtitle>
          </StyledOrderingUnavailable>
        </Render>

        <Render condition={isScheduling}>
          <StyledTimePickerContainer $stack={stack}>
            <div>
              <Label visuallyHidden={false} htmlFor="day_wanted">
                <StyledTimePickerLabel>
                  Change {handoffType} Date:
                  <ChangedMarker show={datePending} />
                </StyledTimePickerLabel>
              </Label>
              <div>
                <StyledItalicSelectContainer>
                  <Select
                    name="day_wanted"
                    title="Date Wanted"
                    value={selectedDay}
                    onChange={(day) => {
                      setDateTouched(true);
                      setSelectedDay(day);
                    }}
                    options={availableDaysOptions}
                    includeLabel={false}
                  />
                </StyledItalicSelectContainer>
              </div>
            </div>
            <StyledTimeSelect
              $showLoading={Boolean(isLoading || isSubmitting)}
              $showError={isError}
            >
              <Render
                condition={hasNoTimes}
                whenTrue={<StyledTimePickerLabel>Change Time:</StyledTimePickerLabel>}
                whenFalse={
                  <>
                    <Label visuallyHidden={false} htmlFor="time_wanted">
                      <StyledTimePickerLabel>
                        Change Time:
                        <ChangedMarker show={timePending} />
                      </StyledTimePickerLabel>
                    </Label>
                    <div>
                      <StyledItalicSelectContainer>
                        <Select
                          name="time_wanted"
                          title="Time Wanted"
                          value={selectedWantedAt}
                          onChange={(selectedDateTime) => {
                            setTimeTouched(true);
                            setSelectedWantedAt(selectedDateTime);
                          }}
                          options={handoffTimeOptions}
                          includeLabel={false}
                          errorMessage={isError ? 'Please select a different time.' : undefined}
                        />
                        {(isLoading || isSubmitting) && (
                          <StyledLoader data-testid="time-picker-loader" />
                        )}
                      </StyledItalicSelectContainer>
                    </div>
                  </>
                }
              />
              {/*
                Mounted for every state, with only its contents conditional — the
                same reason the pending-change region below stays mounted:
                assistive tech announces a text change inside an existing region
                but can miss a region inserted together with its text.

                `role="status"` rather than a second `aria-live="polite"` element:
                it carries the same implicit polite live region, and it keeps the
                pending-change region below the only match for the attribute
                selector the TEAM-14386 tests locate it by.
              */}
              <div role="status">
                <Render condition={hasNoTimes}>
                  <StyledNoTimesMessage data-testid="no-times-available-message">
                    No times available for this date. Please choose another date.
                  </StyledNoTimesMessage>
                </Render>
              </div>
            </StyledTimeSelect>
          </StyledTimePickerContainer>
        </Render>

        {/*
          The live region stays mounted whenever this surface opts in, and only its
          contents are conditional: assistive tech announces a text change inside an
          existing region, but can miss a region inserted together with its text.
        */}
        <Render condition={showPendingChange}>
          <div aria-live="polite">
            <Render condition={hasPendingChange}>
              <StyledPendingChangeWarning>
                <StyledPendingChangeWarningIcon title="Pending change" aria-hidden />
                <StyledPendingChangeWarningText>
                  Menu availability won&apos;t change until you press <strong>Update Time</strong>.
                </StyledPendingChangeWarningText>
              </StyledPendingChangeWarning>
            </Render>
          </div>
        </Render>

        <Render condition={!isOrderingUnavailable}>
          <StyledModalFooter>
            <Render condition={Boolean(onCancel)}>
              <StyledCancelButton
                disabled={isLoading || isSubmitting}
                onClick={() => {
                  if (showPendingChange) {
                    discardPendingChange();
                  }
                  onCancel?.();
                }}
              >
                Cancel
              </StyledCancelButton>
            </Render>
            <StyledConfirmButton
              onClick={() => {
                void commitSelection();
              }}
              disabled={
                (requiresDelivery && !allowCommitWithoutDeliveryAddress) ||
                isLoading ||
                isSubmitting ||
                // Nothing selectable on this day, so there is nothing to commit.
                // Gated on the option count rather than `!selectedWantedAt`: the
                // seeding effect above bails out on an empty list, so the time
                // from the previously selected day survives in state and would
                // otherwise be committed silently against the new date
                // (TEAM-14275 AC2).
                noSelectableTimes
              }
              data-css-override="StyledPrimaryButton"
            >
              Update Time
            </StyledConfirmButton>
          </StyledModalFooter>
        </Render>
      </StyledTimePicker>
    </GenericErrorBoundary>
  );
}
